import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useContext, useRef, useState } from "react";
import Loader from "../Loader";
import Button from "../Button";
import LimitedTextarea from "../LimitedTextarea";
import { showLoginModal } from "../../actions/uiModalActions";
import { StoreContext } from "../../Context/StoreContext";
import { createEventByAI } from "../../services/eventService";
import globals from "../../globals";

interface props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreate: (event: any) => void;
}

function CreateEventByAIModal({ isOpen, setIsOpen, handleCreate }: props) {
  const cancelButtonRef = useRef(null);
  const [isFetching, setIsFetching] = useState(false);
  const [description, setDescription] = useState("");
  const { state, dispatch } = useContext(StoreContext);
  const { auth } = state;
  const handleCreateRoomByAI = async () => {
    const localTime = new Date().toLocaleString();
    if (!description) return;
    if (!auth?.user) {
      showLoginModal(dispatch);
      return;
    }
    try {
      setIsFetching(true);
      const event = (
        await createEventByAI(
          {
            description,
            localTime,
          },
          auth?.user?.token,
        )
      ).data;
      if (event) {
        handleCreate(event);
        setIsOpen(false);
      }
    } catch (err) {
      globals.showModal(
        "Oops!",
        <>
          <div className="bg-white text-gray-600 dark:text-gray-300 dark:bg-navyGray p-4 relative">
            <p className="text-lg">Something went wrong, please try again.</p>
          </div>
        </>,
      );
    } finally {
      setIsFetching(false);
      setDescription("");
    }
  };
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-40 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={setIsOpen}
        style={{ bottom: "0" }}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-overlay bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={`inline-block align-bottom bg-white dark:bg-navyGray
                rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-96 font-ubuntu`}
            >
              <div
                className={`bg-white dark:bg-navyGray dark:border-matteGray p-3 border-b border-frescoWhite `}
              >
                <div className="flex justify-between items-center">
                  <p className="font-medium text-lg  text-black dark:text-frescoWhite  dark:bg-navyGray">
                    Create Event with AI
                  </p>
                  <span
                    className="bg-frescoWhite rounded-full p-1 cursor-pointer"
                    onClick={() => {
                      setIsOpen(false);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-600"
                      fill="none"
                      aria-hidden="true"
                      focusable="false"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </span>
                </div>
              </div>
              <div className=" p-4 relative">
                <LimitedTextarea
                  limit={1000}
                  value={description}
                  rows={5}
                  onChange={(text: string) => {
                    setDescription(text);
                  }}
                  placeHolder="Enter event description..."
                />

                <div className="text-gray-400 text-sm">
                  Describe the event you want to create.
                </div>
              </div>
              <div className=" px-4 py-3 flex flex-row-reverse ">
                {isFetching ? (
                  <Loader />
                ) : (
                  <Button
                    size="vibhut-sm"
                    variant="vibhut-primary"
                    onClick={handleCreateRoomByAI}
                  >
                    Create
                  </Button>
                )}
                <div className="mr-3">
                  <Button
                    size="vibhut-sm"
                    variant="vibhut-secondary"
                    onClick={() => setIsOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default CreateEventByAIModal;
